import { logger } from '@curology/logger/client';
import ErrorIcon from '@curology/ui-components-web/react/Icon/icons/ErrorIcon';
import React from 'react';

import { type FlashMessageProps } from '@/atoms/flashMessages/flashMessagesState';
import { type Brand } from '@/consts/brands';
import COOKIE_KEYS from '@/consts/cookieKeys';
import {
  type TreatmentCategory,
  TreatmentCategoryIds,
} from '@/consts/treatmentCategories';
import { getPaymentErrorMessage } from '@/helpers/productSelectionAndCheckout/getPaymentErrorMessage';
import { extractPatientData } from '@/helpers/productSelectionAndCheckout/helpers';
import { reportUnhandledPaymentErrorToSentry } from '@/helpers/productSelectionAndCheckout/reportUnhandledPaymentErrorToSentry';
import {
  saveProductEligibility,
  updateRegistrationEligibility,
} from '@/services/eligibilityService/actions';
import { ProductSelectionAndCheckoutService } from '@/services/productSelectionAndCheckoutService/client';
import { type IneligibleReasonType } from '@/types/eligibility/eligibility';
import {
  type Patient,
  type ShippingAddressFields,
} from '@/types/productSelectionAndCheckout/types';
import { type PurchaseContext } from '@/types/purchaseContextToggle/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';
import cookie from '@/utils/cookies/client';

export type ActivateSubscriptionResult = {
  success: boolean;
  promoCodeError?: 'Code already claimed.' | 'Code invalid.';
};

export const updateShippingAddress = async (
  flashMessages: FlashMessageProps[],
  setFlashMessages: React.Dispatch<React.SetStateAction<FlashMessageProps[]>>,
  setPatient: React.Dispatch<React.SetStateAction<Patient>>,
  shippingAddressFields: ShippingAddressFields,
  treatmentCategory: TreatmentCategory,
  brand: Brand,
  onIneligible?: (ineligibleReason: IneligibleReasonType) => void,
) => {
  try {
    const data = {
      address1: shippingAddressFields.streetAddress,
      address2: shippingAddressFields.apartmentSuite,
      addressee_name: `${shippingAddressFields.firstName} ${shippingAddressFields.lastName}`,
      city: shippingAddressFields.city,
      firstname: shippingAddressFields.firstName,
      lastname: shippingAddressFields.lastName,
      phoneNumberOptional: true,
      state: shippingAddressFields.state,
      zipcode: shippingAddressFields.zipCode,
    };

    const {
      data: { user, addressValidation },
    } = await ProductSelectionAndCheckoutService.updateShippingAddress(
      brand,
      data,
    );

    try {
      const productEligibilityResponse = await saveProductEligibility({
        brand,
        militaryAddress: addressValidation?.isMilitaryAddress,
        treatmentCategories: treatmentCategory,
      });

      if (productEligibilityResponse?.ineligibleReason) {
        if (onIneligible) {
          onIneligible(productEligibilityResponse.ineligibleReason);
        }

        return false;
      }
    } catch (error: any) {
      logger.warn(`Error updating product eligibility: ${error.message}`);
    }

    try {
      const registrationEligibilityResponse =
        await updateRegistrationEligibility(brand, {
          state: shippingAddressFields.state,
          treatmentCategories: treatmentCategory,
        });

      if (registrationEligibilityResponse?.ineligibleReason) {
        if (onIneligible) {
          onIneligible(registrationEligibilityResponse.ineligibleReason);
        }

        return false;
      }
    } catch (error: any) {
      logger.log(`Error updating registration eligibility: ${error.message}`);
    }

    setPatient(user);

    trackEvent(AnalyticsEvent.CHECKOUT_CONTINUE_BUTTON_CLICKED, {
      card: 'address',
    });

    return true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (exception: any) {
    reportUnhandledPaymentErrorToSentry(exception);
    const message = getPaymentErrorMessage(
      exception?.response?.data,
      'A system error occurred. Please try again.',
    );
    setFlashMessages([
      ...flashMessages,
      {
        children: message,
        icon: <ErrorIcon />,
        iconSize: 'sm',
        state: 'critical',
        title: 'Whoops!',
      },
    ]);

    return false;
  }
};

const trackConversion = (
  brand: Brand,
  invoiceId: string | number,
  referrerSurveyResponse: string | null,
  revenue: string,
  user: Patient,
  treatmentCategory: TreatmentCategory,
) => {
  const products =
    user?.subscriptionItems?.map((subscriptionItem) => ({
      id: subscriptionItem.itemId,
      name: subscriptionItem.name,
    })) ?? [];

  trackEvent(
    AnalyticsEvent.ORDER_COMPLETED,
    {
      currency: 'USD',
      funnelId: cookie.get(COOKIE_KEYS.funnelId),
      invoiceId,
      patient: extractPatientData(user),
      products,
      referrerSurveyResponse,
      revenue,
      treatmentCategories: treatmentCategory,
      treatmentCategoryIds: TreatmentCategoryIds[treatmentCategory],
    },
    {
      gtm: {
        spreadProperties: true,
      },
    },
  );

  // For Google Analytics ecommerce
  // See https://support.google.com/tagmanager/answer/6107169?hl=en
  const capitalizedBrandName = brand[0].toUpperCase() + brand.substring(1);

  trackEvent(
    AnalyticsEvent.GOOGLE_ANALYTICS_TRANSACTION,
    {
      transactionId: invoiceId,
      transactionProducts: [
        {
          name: `${capitalizedBrandName} Subscription`,
          price: revenue,
          quantity: 1,
          sku: '1',
        },
      ],
      transactionTotal: revenue,
    },
    {
      gtm: {
        spreadProperties: true,
      },
    },
  );
};

export const activateSubscription = async (
  brand: Brand,
  flashMessages: FlashMessageProps[],
  patient: Patient,
  setFlashMessages: React.Dispatch<React.SetStateAction<FlashMessageProps[]>>,
  treatmentCategory: TreatmentCategory,
  isTrial = false,
  agencySmallBottlesInSignUpFlag: boolean,
  purchaseContext: PurchaseContext = 'trial',
  promoCode?: string,
): Promise<ActivateSubscriptionResult> => {
  trackEvent(AnalyticsEvent.CREDIT_CARD_CONTINUE_BUTTON_CLICKED, {
    offerId: patient.introOffer?.id,
  });

  const shipmentFrequencyDays = agencySmallBottlesInSignUpFlag ? 30 : 60;

  try {
    const response = isTrial
      ? await ProductSelectionAndCheckoutService.activateTrial(
          brand,
          shipmentFrequencyDays,
          purchaseContext,
          promoCode,
        )
      : await ProductSelectionAndCheckoutService.activateSubscription(
          brand,
          treatmentCategory,
        );

    type ActivateResponse = {
      data?: {
        invoiceId: string | number;
        referrerSurveyResponse: string | null;
        revenue: number;
        user: Patient;
      };
      error?: 'Code already claimed.' | 'Code invalid.';
    };

    const responseData = response.data as ActivateResponse;

    if (responseData.error) {
      return { success: false, promoCodeError: responseData.error };
    }

    const { invoiceId, referrerSurveyResponse, revenue, user } =
      responseData.data!;

    trackConversion(
      brand,
      invoiceId,
      referrerSurveyResponse,
      revenue.toFixed(2),
      user,
      treatmentCategory,
    );

    return { success: true };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (exception: any) {
    reportUnhandledPaymentErrorToSentry(exception);
    const message = getPaymentErrorMessage(
      exception?.response?.data,
      'A system error occurred. Please try again.',
    );
    setFlashMessages([
      ...flashMessages,
      {
        children: message,
        icon: <ErrorIcon />,
        iconSize: 'sm',
        state: 'critical',
        title: 'Whoops!',
      },
    ]);

    return { success: false };
  }
};

export const updateShippingAddressAndActivateSubscription = async (
  brand: Brand,
  flashMessages: FlashMessageProps[],
  patient: Patient,
  setFlashMessages: React.Dispatch<React.SetStateAction<FlashMessageProps[]>>,
  setPatient: React.Dispatch<React.SetStateAction<Patient>>,
  setSubmittingForm: React.Dispatch<React.SetStateAction<boolean>>,
  shippingAddressFields: ShippingAddressFields,
  treatmentCategory: TreatmentCategory,
  isTrial = false,
  onIneligible?: (ineligibleReason: IneligibleReasonType) => void,
  agencySmallBottlesInSignUpFlag = false,
  purchaseContext: PurchaseContext = 'trial',
) => {
  setSubmittingForm(true);

  const shippingAddressSuccessfullyUpdated = await updateShippingAddress(
    flashMessages,
    setFlashMessages,
    setPatient,
    shippingAddressFields,
    treatmentCategory,
    brand,
    onIneligible,
  );

  let activateResult: ActivateSubscriptionResult = { success: false };

  if (shippingAddressSuccessfullyUpdated) {
    activateResult = await activateSubscription(
      brand,
      flashMessages,
      patient,
      setFlashMessages,
      treatmentCategory,
      isTrial,
      agencySmallBottlesInSignUpFlag,
      purchaseContext,
    );
  }

  if (!(shippingAddressSuccessfullyUpdated && activateResult.success)) {
    setSubmittingForm(false);
  }

  return {
    success: shippingAddressSuccessfullyUpdated && activateResult.success,
    promoCodeError: activateResult.promoCodeError,
  };
};
